import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { setToken, getToken, removeToken, setRefreshToken } from '../utils/tokenStorage';
import { authAdapter } from '../utils/authAdapter';
import { normalizeAuthUser } from '../utils/userAccountStatus';

const useUserStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      _hasHydrated: false,
      ipTimezone: null,
      ipOffsetMinutes: null,

      setIpTimezone: (timezone, offsetMinutes) => {
        set({ ipTimezone: timezone, ipOffsetMinutes: offsetMinutes });
      },
      
      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },

      login: (userData, authToken, refreshToken = null) => {
        const normalizedUser = normalizeAuthUser(userData);
        setToken(authToken);
        if (refreshToken) {
          setRefreshToken(refreshToken);
        }
        
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
        
        if (normalizedUser?._id) {
          localStorage.setItem('user_id', normalizedUser._id);
        }
        
        if (normalizedUser?.isAdmin !== undefined) {
          localStorage.setItem('is_admin', normalizedUser.isAdmin.toString());
        }
        
        set({
          user: normalizedUser,
          token: authToken,
          isAuthenticated: true,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: { ...state.user, ...updates },
        }));
      },

      logout: () => {
        // Clear all localStorage items
        removeToken();
        localStorage.removeItem('user_id');
        localStorage.removeItem('is_admin');
        localStorage.removeItem('auth_redirect_url');
        
        // Clear axios headers
        delete axios.defaults.headers.common['Authorization'];
        
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      checkTokenExpiration: () => {
        const { token, logout } = get();
        
        if (!token) return false;
        
        try {
          const decoded = jwtDecode(token);
          const currentTime = Date.now() / 1000;

          if (decoded.exp <= currentTime) {
            logout();
            return false;
          }

          // Check if token expires in the next 5 minutes
          if (decoded.exp < currentTime + 300) {
            return false;
          }
          
          return true;
        } catch (error) {
          logout();
          return false;
        }
      },

      initializeAuth: () => {
        if (!authAdapter.initializeAuthCheck()) {
          return;
        }
        
        const token = getToken();
        const currentState = get();
        
        if (token) {
          try {
            const decoded = jwtDecode(token);
            const currentTime = Date.now() / 1000;
            
            if (decoded.exp > currentTime) {
              axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
              set({
                token: token,
                isAuthenticated: true,
                user: currentState.user,
              });
            } else {
              // Token expired, clear it
              get().logout();
            }
          } catch (error) {
            console.error('Invalid token:', error);
            get().logout();
          }
        }
      },
    }),
    {
      name: 'user-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHasHydrated(true);
          // Re-initialize auth after hydration
          const token = getToken();
          if (token && state.isAuthenticated) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          }
        }
      },
    }
  )
);

export default useUserStore;